import { Link } from "react-router-dom";
import { 
  FileText, Users, BarChart3, Wallet, Tag, PieChart, 
  ArrowRight, CheckCircle, Download, Share2, TrendingUp,
  Bell, Clock, CreditCard
} from "lucide-react";
import { Button } from "../../components/ui/button";
import CursorGlow from "../../components/CursorGlow";

const coreFeatures = [
  {
    icon: FileText,
    title: "AI-Powered Invoicing",
    features: [
      "Smart invoice generation with AI assistance",
      "Automated payment reminders and tracking",
      "Professional PDF export with your branding"
    ]
  },
  {
    icon: Users,
    title: "Client Intelligence",
    features: [
      "Smart client profiles with payment history",
      "Revenue analytics per client",
      "Automated client data capture"
    ]
  },
  {
    icon: BarChart3,
    title: "Financial Dashboard",
    features: [
      "Real-time payment tracking and alerts",
      "Intelligent financial insights",
      "Overdue invoice management"
    ]
  },
  {
    icon: Wallet,
    title: "Automated Cash Flow",
    features: [
      "Automatic cash recording from payments",
      "Expense tracking and categorization",
      "Daily, weekly, monthly summaries"
    ]
  },
  {
    icon: Tag,
    title: "Smart Categories",
    features: [
      "AI-suggested categorization",
      "Custom category management",
      "Unlimited category options"
    ]
  },
  {
    icon: PieChart,
    title: "AI Reports & Insights",
    features: [
      "Intelligent financial analysis",
      "Actionable recommendations",
      "Comprehensive financial statements"
    ]
  }
];

export default function Features() {
  return (
    <div className="min-h-screen bg-emerald-950 text-white overflow-hidden">
      <CursorGlow />
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-emerald-950/90 backdrop-blur-md border-b border-emerald-800/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="flex items-center">
              <img src="/ledgeramt-logo.png" alt="LedgerAMT" className="h-20 sm:h-24 w-auto" />
            </Link>
            
            <div className="hidden md:flex items-center gap-10">
              <Link to="/" className="text-sm font-medium text-emerald-400/80 hover:text-white transition-colors">Home</Link>
              <Link to="/features" className="text-sm font-medium text-emerald-300 hover:text-white transition-colors">Features</Link>
              <Link to="/pricing" className="text-sm font-medium text-emerald-400/80 hover:text-white transition-colors">Pricing</Link>
              <Link to="/about" className="text-sm font-medium text-emerald-400/80 hover:text-white transition-colors">About</Link>
              <Link to="/contact" className="text-sm font-medium text-emerald-400/80 hover:text-white transition-colors">Contact</Link>
            </div>

            <div className="flex items-center gap-4">
              <Link to="/login"><Button variant="ghost" size="sm" className="text-emerald-300 hover:text-white hover:bg-emerald-800/50">Log in</Button></Link>
              <Link to="/pricing"><Button size="sm" className="bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold px-6">Sign up</Button></Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative pt-32 sm:pt-40 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="absolute top-20 left-1/4 w-96 h-96 bg-emerald-500/20 rounded-full blur-3xl"></div>
        <div className="absolute top-40 right-1/4 w-80 h-80 bg-emerald-400/10 rounded-full blur-3xl"></div>
        
        <div className="max-w-4xl mx-auto text-center relative">
          <h1 className="text-5xl sm:text-6xl font-bold mb-8">
            Features built for
            <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-emerald-300">
              real business operations
            </span>
          </h1>
          <p className="text-xl text-emerald-200/80 max-w-2xl mx-auto">
            AI-powered tools to automate your accounting, track payments, and get actionable financial insights.
          </p>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 relative">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-emerald-900/20 to-transparent"></div>
        <div className="max-w-7xl mx-auto relative">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {coreFeatures.map((feature, index) => (
              <div 
                key={index}
                className="group bg-gradient-to-br from-emerald-900/50 to-emerald-950/50 rounded-3xl p-8 border border-emerald-700/30 hover:border-emerald-500/50 transition-all duration-300"
              >
                <div className="w-14 h-14 bg-gradient-to-br from-emerald-500/30 to-emerald-600/20 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 border border-emerald-500/30">
                  <feature.icon className="w-7 h-7 text-emerald-400" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-4">{feature.title}</h3>
                <ul className="space-y-3">
                  {feature.features.map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                      <span className="text-emerald-300/80">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mock Feature Preview */}
      <section className="py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl font-bold mb-6">
                See your cash flow
                <br />
                <span className="text-emerald-400">at a glance</span>
              </h2>
              <p className="text-emerald-300/70 text-lg mb-8 leading-relaxed">
                No more guessing. Know exactly what's pending, what's overdue, and what's actually cash in hand.
              </p>
              <ul className="space-y-4">
                <li className="flex items-center gap-3 text-emerald-200">
                  <div className="w-8 h-8 bg-emerald-500/20 rounded-lg flex items-center justify-center">
                    <TrendingUp className="w-4 h-4 text-emerald-400" />
                  </div>
                  Real-time balance updates
                </li>
                <li className="flex items-center gap-3 text-emerald-200">
                  <div className="w-8 h-8 bg-emerald-500/20 rounded-lg flex items-center justify-center">
                    <Bell className="w-4 h-4 text-emerald-400" />
                  </div>
                  Overdue payment alerts
                </li>
                <li className="flex items-center gap-3 text-emerald-200">
                  <div className="w-8 h-8 bg-emerald-500/20 rounded-lg flex items-center justify-center">
                    <PieChart className="w-4 h-4 text-emerald-400" />
                  </div>
                  Visual cash flow reports
                </li>
              </ul>
            </div>
            
            {/* Mock Dashboard Preview */}
            <div className="relative">
              <div className="bg-gradient-to-br from-emerald-900/80 to-emerald-950 rounded-3xl p-6 border border-emerald-700/30 shadow-2xl">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-white">Cash Flow Summary</h3>
                    <span className="text-emerald-400 text-sm">This month</span>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-emerald-800/30 rounded-2xl p-4">
                      <p className="text-emerald-400/60 text-sm">Cash In</p>
                      <p className="text-2xl font-bold text-emerald-400">$12,450</p>
                    </div>
                    <div className="bg-emerald-800/30 rounded-2xl p-4">
                      <p className="text-emerald-400/60 text-sm">Cash Out</p>
                      <p className="text-2xl font-bold text-rose-400">$4,200</p>
                    </div>
                  </div>
                  
                  {/* Chart bars */}
                  <div className="flex items-end gap-2 h-32 pt-4">
                    {[60, 80, 45, 90, 70, 85, 65].map((height, i) => (
                      <div key={i} className="flex-1 flex flex-col gap-1">
                        <div 
                          className="bg-emerald-500 rounded-t-md transition-all duration-500" 
                          style={{ height: `${height}%` }}
                        ></div>
                        <div 
                          className="bg-rose-500/50 rounded-b-md transition-all duration-500" 
                          style={{ height: `${height * 0.3}%` }}
                        ></div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 relative">
        <div className="absolute inset-0 bg-gradient-to-t from-emerald-900/50 to-transparent"></div>
        <div className="max-w-4xl mx-auto text-center relative">
          <h2 className="text-4xl sm:text-5xl font-bold mb-6">
            Ready to get <span className="text-emerald-400">organized</span>?
          </h2>
          <p className="text-emerald-300/70 text-lg mb-10">
            Choose a plan and start managing your business finances today.
          </p>
          <Link to="/pricing">
            <Button size="lg" className="bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-semibold text-lg px-10 h-14 rounded-xl shadow-lg shadow-emerald-500/30">
              Choose a plan
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-emerald-950 border-t border-emerald-800/30 py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
            <div>
              <img src="/ledgeramt-logo.png" alt="LedgerAMT" className="h-20 w-auto mb-6" />
              <p className="text-emerald-400/60 text-sm">Simple invoices. Clear cash control.</p>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Product</h4>
              <ul className="space-y-3 text-sm text-emerald-400/60">
                <li><Link to="/features" className="hover:text-emerald-400">Features</Link></li>
                <li><Link to="/pricing" className="hover:text-emerald-400">Pricing</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Company</h4>
              <ul className="space-y-3 text-sm text-emerald-400/60">
                <li><Link to="/about" className="hover:text-emerald-400">About</Link></li>
                <li><Link to="/contact" className="hover:text-emerald-400">Contact</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Account</h4>
              <ul className="space-y-3 text-sm text-emerald-400/60">
                <li><Link to="/login" className="hover:text-emerald-400">Log in</Link></li>
                <li><Link to="/pricing" className="hover:text-emerald-400">Sign up</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-emerald-800/30 pt-8 text-center text-sm text-emerald-400/50">
            <p>© {new Date().getFullYear()} LedgerAMT. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
